import React from "react";
import {
  Box,
  Button,
  CheckBox,
  FormField,
  Heading,
  Text,
  TextInput,
} from "grommet";
import { Add, Trash } from "grommet-icons";
import { useTranslation } from "react-i18next";
import { HourInput } from "@shared";
import { useFetchHotel } from "./redux/hooks";

const weekdays = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

export default function ConsumptionScheduleEditor({
  value,
  onChange,
  disabled = false,
}) {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const schedule = value || {};
  const selectedWeekdays = schedule.weekdays || [];
  const timeRanges = schedule.timeRanges || [];

  const update = (changes) => onChange({ ...schedule, ...changes });

  const toggleWeekday = (day, checked) => {
    const nextWeekdays = checked
      ? [...selectedWeekdays, day].sort()
      : selectedWeekdays.filter((item) => item !== day);
    update({ weekdays: nextWeekdays });
  };

  const updateTimeRange = (index, field, nextValue) => {
    const nextRanges = timeRanges.map((range, rangeIndex) =>
      rangeIndex === index ? { ...range, [field]: nextValue } : range,
    );
    update({ timeRanges: nextRanges });
  };

  return (
    <Box gap="small">
      <Heading level={4} margin={{ bottom: "none" }}>
        {t("consumptionSchedule.title")}
      </Heading>
      <Text size="small" color="dark-4">
        {t("consumptionSchedule.emptyHint")}
      </Text>
      <Box direction="row-responsive" gap="small">
        <FormField label={t("consumptionSchedule.startDate")}>
          <TextInput
            type="date"
            disabled={disabled}
            value={schedule.startDate || ""}
            onChange={(event) => update({ startDate: event.target.value })}
          />
        </FormField>
        <FormField label={t("consumptionSchedule.endDate")}>
          <TextInput
            type="date"
            disabled={disabled}
            value={schedule.endDate || ""}
            onChange={(event) => update({ endDate: event.target.value })}
          />
        </FormField>
      </Box>
      <Text weight="bold">{t("consumptionSchedule.weekdays")}</Text>
      <Box direction="row" gap="small" wrap>
        {weekdays.map((weekday, day) => (
          <CheckBox
            key={weekday}
            label={t(`consumptionSchedule.${weekday}`)}
            disabled={disabled}
            checked={selectedWeekdays.includes(day)}
            onChange={(event) => toggleWeekday(day, event.target.checked)}
          />
        ))}
      </Box>
      <Box direction="row" align="center" justify="between">
        <Text weight="bold">{t("consumptionSchedule.timeRanges")}</Text>
        <Button
          icon={<Add size="small" />}
          label={t("consumptionSchedule.addTimeRange")}
          disabled={disabled}
          onClick={() =>
            update({
              timeRanges: [...timeRanges, { start: "09:00", end: "17:00" }],
            })
          }
        />
      </Box>
      {timeRanges.map((range, index) => (
        <Box key={index} direction="row" gap="small" align="end">
          <FormField label={t("consumptionSchedule.from")}>
            <HourInput
              name={`timeRangeStart-${index}`}
              value={range.start || ""}
              timezone={hotel?.timezone}
              disabled={disabled}
              onChange={({ value: nextValue }) =>
                updateTimeRange(index, "start", nextValue)
              }
            />
          </FormField>
          <FormField label={t("consumptionSchedule.to")}>
            <HourInput
              name={`timeRangeEnd-${index}`}
              value={range.end || ""}
              timezone={hotel?.timezone}
              min={range.start || undefined}
              disabled={disabled}
              onChange={({ value: nextValue }) =>
                updateTimeRange(index, "end", nextValue)
              }
            />
          </FormField>
          <Button
            icon={<Trash size="small" />}
            disabled={disabled}
            a11yTitle={t("consumptionSchedule.removeTimeRange")}
            onClick={() =>
              update({
                timeRanges: timeRanges.filter(
                  (_range, rangeIndex) => rangeIndex !== index,
                ),
              })
            }
          />
        </Box>
      ))}
    </Box>
  );
}
